import { REPORT_ACCESS_CONSTANTS } from 'constants/report-access';
import { ODSAvatar, ODSInput, ODSLink, ODSModal, ODSSpinner } from 'kfone-component-library';
import { kf1GetUserNameInitial } from 'kfone-component-library/utilities';
import {
  ButtonTheme,
  InputIconPosition,
  LinkSize,
  ModalActionsAlignment,
  SpinnerSize,
  ToastNotificationVariant,
} from 'kfone-component-library/enums';
import { SearchIcon } from 'kfone-component-library/svgIcons';
import {
  InputLabelProps,
  InputProps,
  SpinnerProps,
  ToastNotificationProps,
} from 'kfone-component-library/types';
import { IReportsListResponse } from 'model/interfaces';
import {
  AccessData,
  RemoveReportAccessParams,
  ReportAccessProps,
  ShareReportAccessPayloadData,
  UserRolesData,
  UsersDetails,
  UsersDetailsURLParams,
} from 'model/types';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useReportAccessService } from 'services';
import { twMerge } from 'tailwind-merge';
import { showToastNotification } from 'utilities';
import './report-access.css';

export const ReportAccess = (props: ReportAccessProps) => {
  const { t } = useTranslation();
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const searchUserElementsRef = useRef<HTMLDivElement>(null); // Ref for the element to be excluded from document clicks
  const labelProps: InputLabelProps = {
    placeholder: t('pe.reportAccess.searchUserPlaceholder'),
  };
  const { fetchUsersDetails, shareReportAccess, removeReportAccess } = useReportAccessService();
  const [searchUserText, setSearchUserText] = useState<string>('');
  const [inputLabelProps, setInputLabelProps] = useState<InputLabelProps>(labelProps);
  const [isUserListVisible, setIsUserListVisible] = useState<boolean>(false);
  const [usersDetailsListLoading, setUsersDetailsListLoading] = useState<boolean>(false);
  const [usersDetailsList, setUsersDetailsList] = useState<UsersDetails | null>(null);
  const [accessUserList, setAccessUserList] = useState<AccessData[]>(
    props.reportData?.sharedWithList || [],
  );
  const [reportAccessActionLoading, setReportAccessActionLoading] = useState<boolean>(false);
  const [removeAccessUser, setRemoveAccessUser] = useState<AccessData | null>(null);
  const spinnerBackdrop: SpinnerProps = {
    id: 'spinner-backdrop',
    hasBackdrop: true,
    size: SpinnerSize.Small,
    loadingText: 'Please wait...',
  };
  const notificationProps: ToastNotificationProps = {
    description: '',
  };
  const searchUserInput: InputProps = {
    id: 'search_user_input',
    labelProps: inputLabelProps,
    inputIcon: {
      icon: !usersDetailsListLoading ? (
        <SearchIcon className='cursor-pointer stroke-2' onClick={() => handleUserSearch()} />
      ) : (
        <ODSSpinner id='search_input_spinner' className='search-input-spinner' />
      ),
      position: InputIconPosition.Right,
    },
    value: searchUserText,
    callOnChangeOnInput: true,
    disabled:
      usersDetailsListLoading ||
      (accessUserList && accessUserList.length >= REPORT_ACCESS_CONSTANTS.MAX_ACCESS_USER_COUNT),
    onChange: (value, event: any) => {
      if (event?.type !== 'blur') {
        setInputLabelProps({
          ...labelProps,
        });
      }
      setSearchUserText(value?.trim());
    },
    onKeyDown: (event) => handleEnterKeyPressed(event),
    onClick: () => {
      if (searchUserText && usersDetailsList) {
        setIsUserListVisible(true);
      }
    },
  };

  const validateEmailAddress = () => {
    // The test() method returns true if the string matches the pattern, false otherwise.
    return emailRegex.test(searchUserText);
  };

  const handleEnterKeyPressed = (event: any) => {
    if (event?.key === 'Enter') {
      handleUserSearch();
    }
  };

  const checkUserAlreadyAccess = () => {
    if (props.reportData?.createdByEmail?.toLowerCase() === searchUserText.toLowerCase()) {
      return true;
    }
    if (accessUserList && accessUserList.length > 0) {
      const isAlreadyHasAccess = accessUserList.some(
        (user) => user.email && user.email.toLowerCase() === searchUserText.toLowerCase(),
      );
      return isAlreadyHasAccess;
    }
    return false;
  };

  const handleUserSearch = () => {
    setIsUserListVisible(false);
    if (accessUserList && accessUserList.length >= REPORT_ACCESS_CONSTANTS.MAX_ACCESS_USER_COUNT) {
      return;
    }
    // Implement user search logic here
    console.log('Searching for user with email:', searchUserText);
    if (!searchUserText) {
      setInputLabelProps({
        ...labelProps,
        error: t('pe.reportAccess.searchTermRequired'),
      });
      return;
    }
    if (!validateEmailAddress()) {
      setInputLabelProps({
        ...labelProps,
        error: t('pe.reportAccess.validSearchTerm'),
      });
      return;
    }
    if (checkUserAlreadyAccess()) {
      setInputLabelProps({
        ...labelProps,
        error: t('pe.reportAccess.userAlreadyHasAccess'),
      });
      return;
    }
    setInputLabelProps({
      ...labelProps,
    });
    getUserDetailsList();
  };

  const hasValidRole = (roles: UserRolesData[]) => {
    return roles.some((role) =>
      REPORT_ACCESS_CONSTANTS.USER_ROLES.includes(role.role_name.toLowerCase()),
    );
  };

  const hasUserPermission = (userDetails: UsersDetails) => {
    if (!userDetails) {
      setInputLabelProps({
        ...labelProps,
        error: t('pe.reportAccess.userNotFound'),
      });
      return false;
    }
    if (
      userDetails &&
      (!userDetails.roles || userDetails.roles.length === 0 || !hasValidRole(userDetails.roles))
    ) {
      setInputLabelProps({
        ...labelProps,
        error: t('pe.reportAccess.userDoesNotHavePermission'),
      });
      return false;
    }
    return true;
  };

  const getUserDetailsList = async () => {
    // Implement logic to fetch and set user details list based on searchUserText
    setUsersDetailsListLoading(true);
    const params: UsersDetailsURLParams = {
      emailId: searchUserText,
      clientId: props.reportData.clientId,
    };
    fetchUsersDetails(params).then(
      (response) => {
        const isUserValid = hasUserPermission(response);
        setUsersDetailsListLoading(false);
        if (isUserValid) {
          setUsersDetailsList(response);
          setIsUserListVisible(true);
        } else {
          setUsersDetailsList(null);
          setIsUserListVisible(false);
        }
      },
      (error) => {
        console.error('Error fetching user details:', error);
        const errorDetails = error?.response?.data?.error_details;
        const isEmailNotInOrganization =
          errorDetails?.code === REPORT_ACCESS_CONSTANTS.IAM_USER_NOT_FOUND_ERROR_CODE;
        setInputLabelProps({
          ...labelProps,
          error: isEmailNotInOrganization
            ? t('pe.reportAccess.emailNotInOrganization')
            : errorDetails?.message || t('pe.reportAccess.serviceUnavailable'),
        });
        setUsersDetailsList(null);
        setUsersDetailsListLoading(false);
        setIsUserListVisible(false);
      },
    );
  };

  const handleReportAccessModelClose = () => {
    const missingInAccessUserList = (props.reportData?.sharedWithList || []).filter((sourceObj) => {
      return !(accessUserList || []).some((targetObj) => sourceObj.id === targetObj.id);
    });
    const addedInAccessUserList = (accessUserList || []).filter((sourceObj) => {
      return !(props.reportData?.sharedWithList || []).some(
        (targetObj) => sourceObj.id === targetObj.id,
      );
    });
    if (missingInAccessUserList.length > 0 || addedInAccessUserList.length > 0) {
      const updatedReportData: IReportsListResponse = {
        ...props.reportData,
        sharedWithList: accessUserList,
      };
      props.onClose(updatedReportData);
    } else {
      props.onClose();
    }
  };

  const handleShareReportAccess = (userData: UsersDetails) => {
    setReportAccessActionLoading(true);
    const payloadData: ShareReportAccessPayloadData = {
      reportId: props.reportData.reportId,
      countryCode: props.reportData.countryCode,
      permissionCode: 'VIEW',
      sharedWith: userData.id,
      clientId: userData.clientId,
      firstName: userData.first_name,
      lastName: userData.last_name,
      email: userData.email,
    };
    shareReportAccess(payloadData).then(
      (response) => {
        console.log('Report access shared successfully:', response);
        if (response) {
          setAccessUserList((prevList) => [
            {
              id: userData.id,
              clientId: userData.clientId,
              firstName: userData.first_name,
              lastName: userData.last_name,
              email: userData.email,
            },
            ...prevList,
          ]);
          setIsUserListVisible(false);
          setSearchUserText('');
          setUsersDetailsList(null);
          setReportAccessActionLoading(false);
          notificationProps.description = t('pe.reportAccess.shareUserSuccess');
          notificationProps.variant = ToastNotificationVariant.Success;
        } else {
          notificationProps.description = t('pe.reportAccess.shareUserError');
          notificationProps.variant = ToastNotificationVariant.Error;
        }
        showToastNotification(notificationProps);
      },
      (error) => {
        console.error('Error sharing report access:', error);
        setReportAccessActionLoading(false);
        notificationProps.description =
          error?.response?.data?.responseMessage || t('pe.reportAccess.shareUserError');
        notificationProps.variant = ToastNotificationVariant.Error;
        showToastNotification(notificationProps);
      },
    );
  };

  const SearchUserListContent = () => {
    return (
      <div
        className='search-user-list-content justify-between'
        onClick={() => handleShareReportAccess(usersDetailsList as UsersDetails)}
      >
        <div className='flex items-center gap-2'>
          <ODSAvatar
            id='avatar-report-owned-by'
            items={{ avatarText: kf1GetUserNameInitial(`${usersDetailsList?.first_name} ${usersDetailsList?.last_name}`) }}
          />
          <div className='user-details'>
            <span className='user-name'>{`${usersDetailsList?.first_name} ${usersDetailsList?.last_name}`}</span>
            <span className='user-email-address'>{usersDetailsList?.email}</span>
          </div>
        </div>
        <div className='tag-item neutral float-right'>
          <span className='tag-label'>{t('pe.reportAccess.viewOnly')}</span>
        </div>
      </div>
    );
  };

  const ReportOwnerContent = () => {
    return (
      <div className='report-owner-content'>
        <ODSAvatar id='avatar-report-owned-by' items={{ avatarText: kf1GetUserNameInitial(props.reportData.createdBy) }} />
        <div className='user-details'>
          <span className='user-name'>{props.reportData.createdBy}</span>
          <span className='user-email-address'>{props.reportData.createdByEmail}</span>
        </div>
        <div className='tag-item info-light'>
          <span className='tag-label'>{t('pe.reportAccess.owner')}</span>
        </div>
      </div>
    );
  };

  const handleRemoveReportAccess = (userData: AccessData) => {
    setRemoveAccessUser(null);
    setReportAccessActionLoading(true);
    const params: RemoveReportAccessParams = {
      reportId: props.reportData.reportId,
      sharedWith: userData.id,
    };
    removeReportAccess(params).then(
      (response) => {
        console.log('Report access removed successfully:', response);
        if (response) {
          setAccessUserList((prevList) =>
            prevList.filter((user) => user.email.toLowerCase() !== userData.email.toLowerCase()),
          );
          setReportAccessActionLoading(false);
          notificationProps.description = t('pe.reportAccess.removeUserSuccess');
          notificationProps.variant = ToastNotificationVariant.Success;
        } else {
          notificationProps.description = t('pe.reportAccess.removeUserError');
          notificationProps.variant = ToastNotificationVariant.Error;
        }
        showToastNotification(notificationProps);
      },
      (error) => {
        console.error('Error removing report access:', error);
        setReportAccessActionLoading(false);
        notificationProps.description =
          error?.response?.data?.responseMessage || t('pe.reportAccess.removeUserError');
        notificationProps.variant = ToastNotificationVariant.Error;
        showToastNotification(notificationProps);
      },
    );
  };

  const AccessUserListContent = () => {
    const getRemoveReportAccessContent = () => {
      return (
        <>
          <Trans
            i18nKey='pe.reportAccess.removeUserModalDescription'
            values={{
              userName: removeAccessUser?.firstName + ' ' + removeAccessUser?.lastName,
            }}
            components={[
              <span key='user-name' className='font-bold text-ods-color-brand-primary-700' />,
            ]}
          />
        </>
      );
    };
    return (
      <div className='access-user-list-container'>
        {accessUserList.map((user, index) => (
          <div key={index} className='access-user-list-content justify-between'>
            <div className='flex items-center gap-2'>
              <ODSAvatar
                id={`avatar-access-user-${index}`}
                items={{ avatarText: kf1GetUserNameInitial(`${user.firstName} ${user.lastName}`) }}
              />
              <div className='user-details'>
                <span className='user-name'>{`${user.firstName} ${user.lastName}`}</span>
                <span className='user-email-address'>{user.email}</span>
              </div>
              <div className='tag-item neutral'>
                <span className='tag-label'>{t('pe.reportAccess.viewOnly')}</span>
              </div>
            </div>
            <ODSLink
              id='access_user_remove_link'
              className='access-user-remove-link'
              title={t('pe.reportAccess.removeUserModalButton')}
              size={LinkSize.Small}
              textUnderline={false}
              text={t('pe.reportAccess.removeUserModalButton')}
              onClick={() => {
                setRemoveAccessUser(user);
              }}
            />
          </div>
        ))}
        {removeAccessUser && (
          <ODSModal
            id='remove_report_access_modal'
            className='remove-report-access-modal'
            hasCloseButton={true}
            isOpen={true}
            onClose={() => setRemoveAccessUser(null)}
            title={t('pe.reportAccess.removeUserModalTitle')}
            bodyContent={getRemoveReportAccessContent()}
            footerButtons={{
              onCancel: () => {
                setRemoveAccessUser(null);
              },
              onSuccess: () => {
                handleRemoveReportAccess(removeAccessUser as AccessData);
              },
              actionsAlignment: ModalActionsAlignment.Between,
              cancelButtonVariant: ButtonTheme.Tertiary,
              successButtonLabel: t('pe.reportAccess.removeUserModalButton'),
            }}
          />
        )}
      </div>
    );
  };

  const getReportAccessContent = () => {
    return (
      <>
        {reportAccessActionLoading && <ODSSpinner {...spinnerBackdrop} />}
        <div
          id='search_user_elements'
          className={twMerge(
            'search-user-elements',
            isUserListVisible && 'search-user-list-container',
          )}
          ref={searchUserElementsRef}
        >
          <div className={twMerge('search-user-input', isUserListVisible && 'px-4 pt-4')}>
            <ODSInput {...searchUserInput} />
          </div>
          {isUserListVisible && <SearchUserListContent />}
        </div>
        <div className='report-title-content'>
          <span className='report-title'>{props.reportData.reportName}</span>
          <span className='report-access-text'>{t('pe.reportAccess.canBeAccessedBy')}</span>
        </div>
        {props.reportData?.createdBy && <ReportOwnerContent />}
        {accessUserList && accessUserList.length > 0 && <AccessUserListContent />}
      </>
    );
  };

  const handleDocumentClick = (event: any) => {
    // Check if the clicked element is NOT the target element or a descendant of it
    if (
      searchUserElementsRef.current &&
      !searchUserElementsRef.current.contains(event.target as Node)
    ) {
      // For example, closing a dropdown or modal if the click is outside it
      setIsUserListVisible(false);
    }
  };

  useEffect(() => {
    if (accessUserList && accessUserList.length >= REPORT_ACCESS_CONSTANTS.MAX_ACCESS_USER_COUNT) {
      setInputLabelProps({
        ...labelProps,
        error: t('pe.reportAccess.maximumUserError', {
          maxAccessUserCount: REPORT_ACCESS_CONSTANTS.MAX_ACCESS_USER_COUNT,
        }),
      });
    } else {
      setInputLabelProps({
        ...labelProps,
      });
    }
  }, [accessUserList]);

  useEffect(() => {
    // Add the event listener when the component mounts
    document.getElementById('report_access_modal')?.addEventListener('click', (event) => {
      handleDocumentClick(event); // Call the handler to check if the click is outside the target element
    });
    // Clean up the event listener when the component unmounts
    return () => {
      document
        .getElementById('report_access_modal')
        ?.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  return (
    <div className='report-access'>
      <ODSModal
        id='report_access_modal'
        className={twMerge(
          'report-access-modal',
          removeAccessUser && 'remove-report-access-modal-opened',
        )}
        hasCloseButton={true}
        isOpen={props.isOpen}
        onClose={handleReportAccessModelClose}
        title={t('pe.reportAccess.title')}
        bodyContent={getReportAccessContent()}
      />
    </div>
  );
};
